<script setup lang="ts">
/**
 * Sortable list for ONE highlight slice (premade or custom) — the reorderable rows inside a
 * section of `PremadeCustomEditor`. Extracted so the parent doesn't repeat the drag plumbing
 * for both slices.
 *
 * DUMB component (props down / events up, no store access). It owns only the presentational
 * drag mechanics:
 * - projects the `HighlightItem[]` slice to the `ManageableItem` shape NovaSortableList needs
 *   (order = array index -> `visualization_order`), and maps the reordered result back to a
 *   `HighlightItem[]` on `reorder`;
 * - renders each saved row via `HighlightListItem`, forwarding `remove`/`edit` intent up.
 *
 * The edit lock, the inline editor, and the "create" row stay in the parent: when a row is
 * being edited the parent supplies its editor via the `editing` slot (scoped with the item),
 * so this component never owns draft/editing state. `lockedId` is the id of the row currently
 * open (or a create) — its siblings' actions are disabled and drag is blocked while set.
 */
import { computed } from "vue";
import type { HighlightItem } from "@/features/experience-shared/highlights-v2/types/highlight-item";
import type { ManageableItem } from "@/types/ManageableItems";
import HighlightListItem from "@/features/experience-shared/highlights-v2/components/HighlightListItem.vue";
import NovaSortableList from "@/ui-kit/NovaSortableList/NovaSortableList.vue";

/** A slice item projected with the `visualization_order` NovaSortableList requires. */
type SortableHighlight = HighlightItem & { visualization_order: number };

const props = withDefaults(
  defineProps<{
    /** The slice to render, in order (array index = order). */
    items: HighlightItem[];
    /** Whole editor is read-only — hides row actions, disables drag. */
    readonly?: boolean;
    /** Rows support inline editing (custom only) — shows the Edit action. */
    editable?: boolean;
    /**
     * Id of the row currently being edited (or a create sentinel). While set, drag is blocked
     * and every other row's actions are disabled. `null` = idle.
     */
    lockedId?: string | null;
    /** data-testid for the underlying sortable list. */
    testid?: string;
  }>(),
  { readonly: false, editable: false, lockedId: null, testid: undefined }
);

const emit = defineEmits<{
  /** Reordered slice (drag-and-drop). */
  (e: "reorder", items: HighlightItem[]): void;
  /** Delete the row with this id. */
  (e: "remove", id: string): void;
  /** Start editing the row with this id (custom only). */
  (e: "edit", id: string): void;
}>();

const isLocked = computed(() => props.lockedId !== null);

/** Projection into the sortable shape (order -> visualization_order). */
const sortableItems = computed<SortableHighlight[]>(() =>
  props.items.map((item, index) => ({ ...item, visualization_order: index + 1 }))
);

/** True when a row other than `id` holds the lock — disables that row's actions. */
function isRowLocked(id: string): boolean {
  return isLocked.value && props.lockedId !== id;
}

// NovaSortableList types its slot item and emitted rows as ManageableItem, but they are the
// exact SortableHighlight objects we passed in via `sortableItems` (only visualization_order is
// reassigned on reorder). This single narrowing bridges that gap so the template and onReorder
// don't each repeat a cast.
function asSlice(item: ManageableItem): SortableHighlight {
  return item as unknown as SortableHighlight;
}

// A slice is homogeneous, so we strip visualization_order and hand the parent a plain
// HighlightItem[] to commit.
function onReorder(reordered: ManageableItem[]): void {
  const next = reordered.map(({ visualization_order: _order, ...item }) => item as HighlightItem);
  emit("reorder", next);
}
</script>

<template>
  <NovaSortableList
    v-slot="{ item }"
    :model-value="sortableItems"
    layout="list"
    :disabled="readonly || items.length <= 1"
    :locked="isLocked"
    :options="{ disableDragHandler: true }"
    class="flex flex-col gap-1"
    :data-testid="testid"
    @update:model-value="onReorder"
  >
    <!-- Editing this row -> parent-supplied editor; else the read-only row. -->
    <slot v-if="lockedId === item.id" name="editing" :item="asSlice(item)" />
    <HighlightListItem
      v-else
      :item="asSlice(item)"
      :index="asSlice(item).visualization_order"
      :readonly="readonly"
      :locked="isRowLocked(item.id)"
      :editable="editable"
      @edit="emit('edit', item.id)"
      @remove="emit('remove', item.id)"
    />
  </NovaSortableList>
</template>
